import React, { useState, useRef, useEffect } from 'react';
import { Input } from './ui/input';

interface VerificationCodeInputProps {
  length?: number;
  onComplete: (code: string) => void;
  disabled?: boolean;
}

export const VerificationCodeInput: React.FC<VerificationCodeInputProps> = ({
  length = 6,
  onComplete,
  disabled = false
}) => {
  const [code, setCode] = useState<string[]>(Array(length).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>(Array(length).fill(null));
  
  // Auto-focus the first input on mount
  useEffect(() => {
    if (inputRefs.current[0] && !disabled) {
      inputRefs.current[0].focus();
    }
  }, [disabled]);
  
  const createRefCallback = (index: number) => (element: HTMLInputElement | null) => {
    inputRefs.current[index] = element;
  };
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const value = e.target.value;
    
    // Only allow digits
    if (!/^\d*$/.test(value)) return;
    
    // Update the code array with the new value (take the last character if multiple were pasted)
    const newCode = [...code];
    newCode[index] = value.substring(value.length - 1);
    setCode(newCode);
    
    // If the value is empty (user deleted), focus the previous input
    if (value === '' && index > 0) {
      inputRefs.current[index - 1]?.focus();
      return;
    }
    
    // Focus the next input if this one has a value
    if (value !== '' && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
    
    // If all inputs are filled, call onComplete
    if (newCode.every(digit => digit !== '') && !newCode.includes('')) {
      onComplete(newCode.join(''));
    }
  };
  
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    // Handle backspace
    if (e.key === 'Backspace') {
      if (code[index] === '' && index > 0) {
        // If current input is empty and backspace is pressed, focus previous input
        inputRefs.current[index - 1]?.focus();
      }
    } 
    // Handle arrow keys
    else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
      e.preventDefault();
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
      e.preventDefault();
    }
  };
  
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>, index: number) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain').trim();
    
    // If pasted data matches our expected pattern (all digits)
    if (/^\d+$/.test(pastedData)) {
      const digits = pastedData.split('').slice(0, length);
      const newCode = [...code];
      
      // Fill in as many inputs as we have digits
      digits.forEach((digit, i) => {
        if (index + i < length) {
          newCode[index + i] = digit;
        }
      });
      
      setCode(newCode);
      
      // Focus the appropriate input after paste
      const nextInputIndex = Math.min(index + digits.length, length - 1);
      inputRefs.current[nextInputIndex]?.focus();
      
      // If all inputs are filled, call onComplete
      if (newCode.every(digit => digit !== '')) {
        onComplete(newCode.join(''));
      }
    }
  };
  
  return (
    <div className="flex gap-2 justify-center">
      {Array.from({ length }).map((_, index) => (
        <Input
          key={index}
          ref={createRefCallback(index)}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={1}
          className="w-11 h-12 text-center text-lg font-semibold"
          value={code[index]}
          onChange={e => handleChange(e, index)}
          onKeyDown={e => handleKeyDown(e, index)}
          onPaste={e => handlePaste(e, index)}
          disabled={disabled}
          aria-label={`Verification code digit ${index + 1}`}
        />
      ))}
    </div>
  );
}; 